import React, { useCallback, useEffect, useState } from "react";
import { HostEditsAPI } from "../../api/HostEditsAPI";
import { Change } from "../data-quality/ChangeView";
import { FIELD_LABELS, formatValue } from "../data-quality/proposalFormat";
import "../data-quality/DataQuality.css";
import "./HostEdits.css";

// Edits hosts saved for markers that are already live. The marker stays approved with its old
// data until the admin applies the edit here (the approval merges it) or discards it; the
// pending list below only holds markers that were never approved.

export const errorText = (err) => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message;
  if (status === 409 && message) return message;
  if (status === 404) return "Das Event gibt es nicht mehr (archiviert oder gelöscht).";
  if (status === 401 || status === 403) return "Keine Berechtigung – bitte neu anmelden.";
  return message || err?.message || "Speichern fehlgeschlagen – bitte noch einmal versuchen.";
};

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

function HostEditCard({ edit, state, onApply, onDiscard }) {
  const busy = state?.status === "saving";
  const changes = edit.changes ?? [];
  return (
    <section className="dq-event he-card" aria-label={edit.title}>
      <header className="dq-event-head">
        <div className="dq-event-meta">
          <span className="dq-event-date">{edit.eventDate ? formatValue("eventDate", edit.eventDate) : edit.markerType}</span>
          <h3 className="dq-event-title">{edit.title}</h3>
          <span className="dq-muted">
            ID {edit.markerId}
            {edit.hostCode && <> · Host-Code <span className="he-mono">{edit.hostCode}</span></>}
            {edit.instagramName && <> · @{edit.instagramName}</>}
            {" · "}gespeichert {fmtDateTime(edit.savedAt)}
          </span>
        </div>
        <div className="he-actions">
          <button type="button" className="dq-btn dq-btn--reject" onClick={onDiscard} disabled={busy}>
            Verwerfen
          </button>
          <button type="button" className="dq-btn dq-btn--accept" onClick={onApply} disabled={busy}>
            {busy ? <span className="dq-spinner" aria-hidden="true" /> : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden="true">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            )}
            Übernehmen
          </button>
        </div>
      </header>
      {state?.error && <p className="dq-error he-error" role="alert">{state.error}</p>}
      {changes.length === 0 ? (
        <p className="dq-muted he-none">Keine Änderung gegenüber dem Live-Stand.</p>
      ) : (
        <ul className="dq-rows">
          {changes.map((c) => (
            <li key={c.field} className="dq-row he-row">
              <div className="dq-row-body">
                <span className="dq-field">{FIELD_LABELS[c.field] ?? c.field}</span>
                <Change p={c} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function HostEdits({ refreshKey = 0 }) {
  const [edits, setEdits] = useState([]);
  const [error, setError] = useState(null);
  const [states, setStates] = useState({}); // marker id → { status, error }

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await HostEditsAPI.list();
      setEdits(Array.isArray(rows) ? rows : []);
      setStates({});
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const decide = async (edit, action) => {
    setStates((prev) => ({ ...prev, [edit.markerId]: { status: "saving" } }));
    try {
      await (action === "apply"
        ? HostEditsAPI.apply(edit.markerId, edit.savedAt)
        : HostEditsAPI.discard(edit.markerId, edit.savedAt));
      setEdits((prev) => prev.filter((e) => e.markerId !== edit.markerId));
    } catch (e) {
      setStates((prev) => ({ ...prev, [edit.markerId]: { status: "open", error: errorText(e) } }));
    }
  };

  if (!error && edits.length === 0) return null;

  return (
    <div className="dq-page he-section" aria-label="Änderungen an freigegebenen Events">
      <h2 className="dq-section-title">
        Änderungen an freigegebenen Events <span className="dq-filter-count">{edits.length}</span>
      </h2>
      {error && <div className="error-state" role="alert">Änderungen konnten nicht geladen werden: {error}</div>}
      <div className="dq-groups">
        {edits.map((edit) => (
          <HostEditCard
            key={edit.markerId}
            edit={edit}
            state={states[edit.markerId]}
            onApply={() => decide(edit, "apply")}
            onDiscard={() => decide(edit, "discard")}
          />
        ))}
      </div>
    </div>
  );
}
